import { CalendarDays, Clapperboard, Info, RefreshCw } from 'lucide-react'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import type { InstagramMetric, InstagramReel, PeriodFilter } from '../../api/instagramAnalytics'
import { isApiError } from '../../api/client'
import { useViewState } from '../../lib/navigation'
import {
  ANALYTICS_PERIODS,
  SORT_GROUPS,
  dateLabel,
  metricHint,
  metricText,
  moscowToday,
  periodLabel,
  validDateRange,
  type AnalyticsSort,
} from '../../lib/instagramAnalytics'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Select } from '../Select'
import { Spinner } from '../Spinner'

export function Panel({
  title,
  aside,
  children,
  className = '',
}: {
  title: string
  aside?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`min-w-0 rounded-xl border border-line bg-card p-4 desk:p-5 ${className}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  )
}
export function InfoHint({ children }: { children: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className="inline-flex size-5 shrink-0 items-center justify-center rounded align-middle text-muted hover:text-fg"
        aria-label={children}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={children}
        onClick={(event) => {
          event.stopPropagation()
          setOpen(true)
        }}
      >
        <Info size={14} />
      </button>
      {open && (
        <Dialog title="О показателе" onClose={() => setOpen(false)}>
          <p className="pb-2 text-sm">{children}</p>
        </Dialog>
      )}
    </>
  )
}
export function Metric({
  label,
  metric,
  rate = false,
  suffix,
  description,
}: {
  label: string
  metric?: InstagramMetric
  rate?: boolean
  suffix?: string
  description?: string
}) {
  const hint = [description, metricHint(metric)].filter(Boolean).join(' ')
  return (
    <div className="min-w-0">
      <dt className="mb-1 flex min-w-0 items-start gap-1 text-xs text-muted">
        <span className="min-w-0 break-words">{label}</span>
        {hint && <InfoHint>{hint}</InfoHint>}
      </dt>
      <dd className="text-xl font-semibold tabular-nums tracking-tight desk:text-2xl">
        {metricText(metric, rate)}
        {metric?.status === 'available' && metric.value !== null && suffix && (
          <span className="ml-1 text-sm font-normal text-muted">{suffix}</span>
        )}
      </dd>
    </div>
  )
}
export function MetricRows({
  fields,
  metrics,
  rate = false,
}: {
  fields: [string, string][]
  metrics: Record<string, InstagramMetric>
  rate?: boolean
}) {
  return (
    <dl className="divide-y divide-line">
      {fields.map(([key, label]) => (
        <div className="flex min-h-10 items-center justify-between gap-4 py-2" key={key}>
          <dt className="flex min-w-0 items-center gap-1 text-sm text-muted">
            {label}
            {metricHint(metrics[key]) && <InfoHint>{metricHint(metrics[key])}</InfoHint>}
          </dt>
          <dd className="shrink-0 font-medium tabular-nums">{metricText(metrics[key], rate)}</dd>
        </div>
      ))}
    </dl>
  )
}
export function Missing({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg bg-surface/60 px-3 py-3 text-sm text-pretty text-muted">{children}</p>
  )
}
export function QueryState({
  pending,
  error,
  retry,
}: {
  pending?: boolean
  error?: unknown
  retry: () => unknown
}) {
  if (pending)
    return (
      <div
        className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted"
        role="status"
      >
        <Spinner size={20} />
        Загружаю аналитику…
      </div>
    )
  if (error)
    return (
      <div className="rounded-xl border border-line bg-card p-5" role="alert">
        <p className="mb-3 text-sm">
          {isApiError(error, 422)
            ? 'Проверьте период: даты должны существовать, заканчиваться не позже сегодняшнего дня и охватывать не больше 730 дней.'
            : isApiError(error, 403)
              ? 'Эта аналитика доступна владельцу. Обновите страницу после изменения роли.'
              : isApiError(error, 404)
                ? 'Подборка или ролик больше не доступны. Вернитесь к списку.'
                : 'Не получилось загрузить аналитику. Проверьте соединение и повторите.'}
        </p>
        <Button onClick={() => void retry()}>
          <RefreshCw size={15} />
          Повторить
        </Button>
      </div>
    )
  return null
}
export function PeriodPicker({
  value,
  onChange,
  allowAll = false,
  label = 'Период аналитики',
}: {
  value: PeriodFilter & { all_time?: boolean }
  onChange: (value: PeriodFilter & { all_time?: boolean }) => void
  allowAll?: boolean
  label?: string
}) {
  const stateKey = `analytics.period.${JSON.stringify(value)}`
  const [custom, setCustom] = useViewState(`${stateKey}.custom`, Boolean(value.date_from))
  const [from, setFrom] = useViewState(`${stateKey}.from`, value.date_from ?? '')
  const [to, setTo] = useViewState(`${stateKey}.to`, value.date_to ?? '')
  const [error, setError] = useState('')
  const id = useId()
  const today = moscowToday()
  const choose = (selected: string) => {
    setError('')
    setCustom(selected === 'custom')
    if (selected === 'all') onChange({ all_time: true })
    else if (selected !== 'custom') onChange({ period_days: Number(selected) })
  }
  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-2 desk:w-auto desk:items-end">
      <Select
        label={label}
        value={custom ? 'custom' : value.all_time && allowAll ? 'all' : (value.period_days ?? 30)}
        onChange={(e) => choose(e.target.value)}
        className="w-full desk:w-48"
      >
        {allowAll && <option value="all">За всё время</option>}
        {ANALYTICS_PERIODS.map((days) => (
          <option key={days} value={days}>
            {periodLabel(days)}
          </option>
        ))}
        <option value="custom">Выбрать даты</option>
      </Select>
      {custom && (
        <form
          className="grid w-full min-w-0 grid-cols-1 items-end gap-2 desk:w-auto desk:grid-cols-[22.5rem_auto]"
          onSubmit={(e) => {
            e.preventDefault()
            if (!validDateRange(from, to)) {
              setError('Укажите даты: начало не позже окончания')
              return
            }
            if (to > today) {
              setError('Дата окончания не может быть позже сегодняшнего дня по Москве')
              return
            }
            if ((Date.parse(to) - Date.parse(from)) / 86_400_000 >= 730) {
              setError('Выберите период не больше 730 дней')
              return
            }
            setError('')
            onChange({ date_from: from, date_to: to })
          }}
        >
          <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-2 desk:grid-cols-2">
            <label className="min-w-0 text-xs text-muted">
              С
              <input
                aria-label="Начало периода"
                aria-describedby={error ? id : undefined}
                required
                type="date"
                className="analytics-period-date mt-1 block box-border h-11 w-full min-w-0 max-w-full appearance-none rounded-lg border border-line bg-card px-2 py-[calc(0.625rem-1px)] text-base leading-6 text-fg desk:h-8 desk:py-[calc(0.375rem-1px)] desk:text-sm desk:leading-5"
                value={from}
                max={to && to < today ? to : today}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="min-w-0 text-xs text-muted">
              По
              <input
                aria-label="Конец периода"
                aria-describedby={error ? id : undefined}
                required
                type="date"
                className="analytics-period-date mt-1 block box-border h-11 w-full min-w-0 max-w-full appearance-none rounded-lg border border-line bg-card px-2 py-[calc(0.625rem-1px)] text-base leading-6 text-fg desk:h-8 desk:py-[calc(0.375rem-1px)] desk:text-sm desk:leading-5"
                value={to}
                max={today}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>
          <Button type="submit" className="w-full desk:w-auto">
            <CalendarDays size={14} />
            Применить
          </Button>
          {error && (
            <p id={id} role="alert" className="col-span-full text-xs text-danger">
              {error}
            </p>
          )}
        </form>
      )}
      <span className="text-[11px] text-muted">Москва (UTC+3)</span>
    </div>
  )
}
export function SortPicker({
  value,
  onChange,
}: {
  value: AnalyticsSort
  onChange: (value: AnalyticsSort) => void
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 text-xs text-muted">
      <span aria-hidden="true">Сначала</span>
      <Select
        label="Сначала"
        value={value}
        onChange={(e) => onChange(e.target.value as AnalyticsSort)}
        className="w-full desk:w-72"
      >
        <option value="latest">Последние</option>
        {SORT_GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
    </div>
  )
}
export function ReelThumbnail({
  reel,
  className = '',
}: {
  reel: Pick<InstagramReel, 'thumbnail_url'>
  className?: string
}) {
  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface text-muted ${className}`}
    >
      {reel.thumbnail_url ? (
        <img
          loading="lazy"
          src={reel.thumbnail_url}
          alt=""
          className="size-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        <Clapperboard size={24} strokeWidth={1.5} />
      )}
      <span className="absolute right-1 bottom-1 rounded bg-card/90 px-1 text-[10px]">Reels</span>
    </div>
  )
}
export function ReelTitle({ reel, expanded = false }: { reel: Pick<InstagramReel, 'caption' | 'shortcode' | 'published_at'> & Partial<Pick<InstagramReel, 'trial_status'>>; expanded?: boolean }) {
  return (
    <>
      <span
        className={`${expanded ? 'line-clamp-2 min-h-[2.7em]' : 'block truncate'} text-sm font-medium`}
      >
        {reel.caption || reel.shortcode}
      </span>
      <span className="mt-1 block text-xs text-muted">
        {dateLabel(reel.published_at)}
        {reel.trial_status === 'confirmed_trial' && ' · Пробный Reels'}
        {reel.trial_status === 'unknown' && ' · Пробность не указана'}
      </span>
    </>
  )
}

export interface ChartPoint {
  x: number
  y: number | null
  label: string
}
export interface ChartSeries {
  id: string
  label: string
  color: string
  dashed?: boolean
  points: ChartPoint[]
}
type PlotPoint = ChartPoint & { seriesId: string; seriesLabel?: string; color: string }
/** Пропуски в данных разрывают линию; незаписанная история не превращается в нули. */
export function LineChart({
  points: singlePoints = [],
  series,
  label,
  percent = false,
  onSelect,
  selectedX,
  percentMax,
  emptyMessage = 'За этот период нет записанных точек графика.',
}: {
  points?: ChartPoint[]
  series?: ChartSeries[]
  label: string
  percent?: boolean
  onSelect?: (point: ChartPoint) => void
  selectedX?: number | null
  /** Для распределений лайков Instagram масштабирует ось по максимуму среза. */
  percentMax?: number
  emptyMessage?: string
}) {
  const [hovered, setHovered] = useState<PlotPoint | null>(null)
  const container = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState(680)
  const lines: ChartSeries[] = series ?? [{ id: 'default', label, color: 'var(--link)', points: singlePoints }]
  const points: PlotPoint[] = lines.flatMap((line) => line.points.map((point) => ({
    ...point, seriesId: line.id, seriesLabel: series ? line.label : undefined, color: line.color,
  })))
  if (series) points.sort((a, b) => a.x - b.x)
  const known = points.filter(
    (point): point is PlotPoint & { y: number } => point.y !== null && Number.isFinite(point.y),
  )
  const hasData = known.length > 0
  useEffect(() => {
    if (!container.current) return
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setChartWidth(Math.max(280, entry.contentRect.width))
    })
    observer.observe(container.current)
    return () => observer.disconnect()
  }, [hasData])
  if (!known.length) return <Missing>{emptyMessage}</Missing>
  const width = chartWidth
  const height = 220
  const left = 52
  const right = 16
  const top = 16
  const bottom = 34
  const max = percent ? (percentMax ?? 100) : Math.max(1, ...known.map((p) => p.y))
  const min = Math.min(0, ...known.map((p) => p.y))
  const xmin = Math.min(...points.map((p) => p.x))
  const xmax = Math.max(xmin + 1, ...points.map((p) => p.x))
  const x = (v: number) => left + ((v - xmin) / (xmax - xmin)) * (width - left - right)
  const y = (v: number) => top + ((max - v) / (max - min)) * (height - top - bottom)
  const paths = lines.map((line) => {
    let path = ''
    let connected = false
    for (const point of line.points) {
      if (point.y === null || !Number.isFinite(point.y)) {
        connected = false
        continue
      }
      path += `${connected ? 'L' : 'M'}${x(point.x)},${y(point.y)} `
      connected = true
    }
    return { ...line, path }
  })
  const active =
    hovered ??
    (selectedX !== undefined && selectedX !== null ? known.find((p) => p.x === selectedX) : null)
  return (
    <div ref={container} className="min-w-0">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full overflow-visible"
        role="img"
        aria-label={label}
      >
        {[max, (max + min) / 2, min].map((value, index) => (
          <g key={index}>
            <line x1={left} x2={width - right} y1={y(value)} y2={y(value)} stroke="var(--line)" />
            <text x={left - 9} y={y(value) + 4} textAnchor="end" fontSize="12" fill="var(--muted)">
              {new Intl.NumberFormat('ru-RU', {
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(value)}
              {percent ? '%' : ''}
            </text>
          </g>
        ))}
        {paths.map((line) => <path
          key={line.id}
          data-series={series ? line.id : undefined}
          d={line.path}
          fill="none"
          stroke={line.color}
          strokeDasharray={line.dashed ? '6 4' : undefined}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />)}
        {known.map((p, index) => (
          <g key={`${p.seriesId}-${p.x}-${index}`}>
            <circle
              cx={x(p.x)}
              cy={y(p.y)}
              r={known.length === 1 || (active?.x === p.x && active.seriesId === p.seriesId) ? 4 : series ? 3 : 2}
              fill={p.color}
            />
            <circle
              cx={x(p.x)}
              cy={y(p.y)}
              r={series ? 22 : 12}
              fill="transparent"
              tabIndex={onSelect || series ? 0 : undefined}
              role={onSelect || series ? 'button' : undefined}
              aria-label={`${p.seriesLabel ? `${p.seriesLabel}: ` : ''}${p.label}: ${p.y}${percent ? '%' : ''}`}
              className={onSelect || series ? 'cursor-pointer' : ''}
              onMouseEnter={() => setHovered(p)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(p)}
              onBlur={() => setHovered(null)}
              onClick={() => { if (series) setHovered(p); onSelect?.(p) }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (series) setHovered(p)
                  onSelect?.(p)
                }
              }}
            >
              <title>
                {p.seriesLabel ? `${p.seriesLabel}: ` : ''}{p.label}:{' '}
                {new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(p.y)}
                {percent ? '%' : ''}
              </title>
            </circle>
          </g>
        ))}
        {[points[0], points[points.length - 1]].map(
          (p, i) =>
            p && (
              <text
                key={i}
                x={i ? width - right : left}
                y={height - 8}
                textAnchor={i ? 'end' : 'start'}
                fontSize="12"
                fill="var(--muted)"
              >
                {p.label}
              </text>
            ),
        )}
      </svg>
      {series && <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted" aria-label="Источники графика">
        {series.map((line) => <span key={line.id} className="inline-flex items-center gap-2">
          <svg width="20" height="10" aria-hidden="true"><line x1="0" x2="20" y1="5" y2="5" stroke={line.color} strokeWidth="2.5" strokeDasharray={line.dashed ? '6 4' : undefined} /></svg>
          {line.label}
        </span>)}
      </div>}
      {active && (
        <p className="mt-1 text-center text-xs text-muted" aria-live="polite">
          {active.seriesLabel ? `${active.seriesLabel} · ` : ''}{active.label} ·{' '}
          {new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(active.y ?? 0)}
          {percent ? '%' : ''}
        </p>
      )}
    </div>
  )
}
